const system = {
  loading: "Checking per-user application data isolation…",
  unavailable: {
    title: "System health unavailable",
    retry: "Retry",
    fetchFailed: "Failed to fetch system status.",
    healthFailed: "System health check failed.",
  },
  header: {
    title: "System Architecture & Health",
    description: "This page is system-wide and Super-Admin only. It verifies that every application record—including sources, filters, AI preferences, monitored posts, Inbox state, and destinations—is owned by one member. Credentials remain managed from each member’s My Destinations workspace.",
    scope: "System administration scope",
    notice: "System health notice",
    cutoverTitle: "Full application ownership cutover pending",
    cutoverDescription: "A coordinated database and backend release is still required before every source, filter, AI setting, monitored post, Inbox item, and destination is structurally required to have an owner.",
    supabaseEndpoint: "Supabase endpoint:",
    notConfigured: "Not configured",
    refresh: "Refresh health",
  },
  overview: {
    backendLabel: "Backend Runtime",
    backendTitle: "Normalized PostgreSQL",
    backendActive: "Backend active",
    backendUnavailable: "Backend unavailable",
    architectureLabel: "Multi-user Architecture",
    architectureTitle: "Workspace Isolation",
    isolated: "Personal data isolated",
    cutoverIncomplete: "Cutover incomplete",
    automationLabel: "Automation",
    automationTitle: "Collection & Cleanup",
    jobsActive: "Jobs active",
    automationIncomplete: "Automation incomplete",
    securityLabel: "Data Security",
    securityTitle: "Backend-Owned Tables",
    protected: "{{protected}}/{{expected}} protected",
  },
  monitoring: {
    title: "Personal Monitoring Data",
    description: "Sources and crawled Telegram posts are stored separately for each member, even when two members monitor the same public channel.",
    sourceChannels: "Source Channels",
    monitoredPosts: "Monitored Posts · 24h",
  },
  workspace: {
    title: "Personal Workspace Data",
    description: "Inbox workflow and publishing configuration are also separated by ownership principal. Counts below are aggregate system health metrics only.",
    destinationTargets: "Destination Targets",
    inboxRows: "Inbox Workflow Rows",
    publishedWorkflow: "Published Workflow",
    supabaseMembers: "Supabase Members",
    legacyMembers: "Legacy Members",
    unownedRows: "Unowned App Rows",
    publishStates: "Per-user publish states",
    durableIdentities: "Active durable identities",
    usernameOwned: "Username-owned workspaces",
    shouldRemainZero: "Should remain 0",
    owners_one: "{{formattedCount}} owner",
    owners_other: "{{formattedCount}} owners",
  },
  runtime: {
    title: "Runtime Data Boundaries",
    description: "All application tables are personal. Only the legacy compatibility record is system-level, and browser clients do not receive direct database write access.",
    scopes: {
      personal: "Personal",
      compatibility: "Compatibility",
    },
    tables: {
      source_channels: "Personal Sources",
      filters: "Personal Filters",
      destination_targets: "Personal Destinations",
      ai_settings: "Personal AI Settings",
      posts: "Personal Monitored Posts",
      user_inbox_items: "Personal Inbox State",
      curator_settings: "Legacy Compatibility",
    },
  },
  isolation: {
    title: "Workspace Isolation Checks",
    description: "These checks verify that no application data can exist outside a member workspace.",
    ownershipTitle: "Complete application ownership",
    ownershipDescription: "Sources, filters, AI settings, posts, and destinations require an owner.",
    inboxTitle: "Content Inbox isolation",
    inboxDescription: "Review/edit/publish state joins only to posts owned by the same member.",
    orphanTitle: "Orphan application-data check",
    orphanDescription: "Every production application row should have an owner.",
    ready: "Ready",
    missing: "Missing",
    cutoverPending: "Cutover pending",
    noOrphans: "No orphans",
    unowned: "{{count}} unowned",
    note: "Source definitions, filters, AI preferences, monitored post text, status, history, and errors are never treated as global configuration. Individual Telegram bot credentials remain in user-scoped Vault secrets.",
  },
  automation: {
    title: "Collection Automation",
    description: "The scheduler enumerates owners with enabled sources and runs collection in each isolated workspace using that member’s filters and post records.",
    cronInstalled: "pg_cron installed",
    cronMissing: "pg_cron missing",
    netInstalled: "pg_net installed",
    netMissing: "pg_net missing",
    empty: "No TGReposter collection jobs were found.",
    jobs: {
      import: "Source Inbox Import",
      cleanup: "Rolling Inbox Cleanup",
    },
    schedules: {
      everyFiveMinutes: "Every 5 minutes",
      everyHour: "Every hour",
    },
    succeeded: "Succeeded",
    active: "Active",
    lastRunFailed: "Last run failed",
    disabled: "Disabled",
    lastRun: "Last run: {{time}}",
    noRun: "No run recorded",
  },
  security: {
    title: "Migration-managed, secure-by-default runtime",
    description: "Structural changes are versioned through Supabase migrations. All application configuration and content are member-owned, and sensitive writes are handled by authenticated backend routes rather than direct browser database access.",
  },
} as const;

export default system;
